import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TrackToggle,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  CallEnd,
  ChatBubbleOutline,
  ClosedCaption,
  GridView,
  Groups,
  MoreHoriz,
  Share,
  ViewAgenda,
} from '@mui/icons-material';
import { shortRoomCode, isAgentParticipant } from '../../utils/participantMeta';
import SageIcon from '../icons/SageIcon';

function formatClock(date) {
  try {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Collapse right-side actions into a "More" menu below this footer width. */
const COMPACT_FOOTER_WIDTH = 900;

export default function MeetingFooter({
  roomName,
  isHost,
  onLeave,
  onOpenPeople,
  onOpenShare,
  onAskSage,
  sageOpen,
  peopleOpen,
  captionsOn,
  captionsAvailable,
  onToggleCaptions,
  layout,
  onCycleLayout,
  sageAvailable,
  isChatOpen,
  onChatToggle,
  forceCompact = false,
}) {
  const room = useRoomContext();
  const { isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const participants = useParticipants();
  const footerRef = useRef(null);
  const moreRef = useRef(null);
  const [now, setNow] = useState(() => new Date());
  const [compact, setCompact] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = footerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      const width = el.getBoundingClientRect().width;
      setCompact(forceCompact || width < COMPACT_FOOTER_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [forceCompact]);

  useEffect(() => {
    if (!compact) setMoreOpen(false);
  }, [compact]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onPointerDown = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setMoreOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moreOpen]);

  const peopleCount = useMemo(
    () => (participants || []).filter((p) => !isAgentParticipant(p)).length,
    [participants]
  );

  const handleEnd = useCallback(() => {
    try {
      room?.disconnect();
    } catch {
      /* ignore */
    }
    onLeave?.();
  }, [room, onLeave]);

  const runAndClose = useCallback((action) => {
    setMoreOpen(false);
    action?.();
  }, []);

  const captionsLabel = captionsOn
    ? 'Hide captions'
    : captionsAvailable
      ? 'Show captions'
      : 'Captions require Sage';
  const micLabel = isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone';
  const camLabel = isCameraEnabled ? 'Turn off camera' : 'Turn on camera';
  const sageLabel = sageOpen ? 'Close Sage' : 'Ask Sage';
  const layoutLabel =
    layout === 'grid' ? 'Switch to focus layout' : 'Switch to grid layout';
  const chatLabel = isChatOpen ? 'Close chat' : 'Open chat';
  const peopleLabel = peopleOpen
    ? 'Close participants'
    : `Participants (${peopleCount})`;

  const showSage = isHost && sageAvailable !== false;
  const moreActive = sageOpen || peopleOpen || isChatOpen || layout === 'focus';

  const secondaryActions = (
    <>
      {showSage ? (
        <button
          type="button"
          className={`gg-ctrl gg-ctrl--square gg-ctrl--sage${
            sageOpen ? ' gg-ctrl--on' : ''
          }`}
          onClick={() => runAndClose(onAskSage)}
          title={sageLabel}
          aria-pressed={Boolean(sageOpen)}
          aria-label={sageLabel}
        >
          <SageIcon size={18} />
          <span className="gg-ctrl__sage-label">Sage</span>
        </button>
      ) : null}
      <button
        type="button"
        className={`gg-ctrl gg-ctrl--square${peopleOpen ? ' gg-ctrl--on' : ''}`}
        onClick={() => runAndClose(onOpenPeople)}
        title={peopleLabel}
        aria-pressed={Boolean(peopleOpen)}
        aria-label={peopleLabel}
      >
        <Groups />
        <span className="gg-ctrl__count">{peopleCount}</span>
      </button>
      <button
        type="button"
        className={`gg-ctrl gg-ctrl--square${layout === 'focus' ? ' gg-ctrl--on' : ''}`}
        onClick={() => runAndClose(onCycleLayout)}
        title={layoutLabel}
        aria-pressed={layout === 'focus'}
        aria-label={layoutLabel}
      >
        {layout === 'grid' ? <ViewAgenda /> : <GridView />}
      </button>
      <button
        type="button"
        className={`gg-ctrl gg-ctrl--square${isChatOpen ? ' gg-ctrl--on' : ''}`}
        onClick={() => runAndClose(onChatToggle)}
        title={chatLabel}
        aria-pressed={Boolean(isChatOpen)}
        aria-label={chatLabel}
      >
        <ChatBubbleOutline />
      </button>
      {isHost ? (
        <button
          type="button"
          className="gg-ctrl gg-ctrl--square gg-ctrl--share"
          onClick={() => runAndClose(onOpenShare)}
          title="Share invite link"
          aria-label="Share invite link"
        >
          <Share />
        </button>
      ) : null}
    </>
  );

  const moreMenuItems = (
    <>
      {showSage ? (
        <button
          type="button"
          role="menuitem"
          className={`gg-footer-more__item${sageOpen ? ' is-active' : ''}`}
          onClick={() => runAndClose(onAskSage)}
        >
          <SageIcon size={16} />
          <span>{sageLabel}</span>
        </button>
      ) : null}
      <button
        type="button"
        role="menuitem"
        className={`gg-footer-more__item${peopleOpen ? ' is-active' : ''}`}
        onClick={() => runAndClose(onOpenPeople)}
      >
        <Groups fontSize="small" />
        <span>{peopleLabel}</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className={`gg-footer-more__item${layout === 'focus' ? ' is-active' : ''}`}
        onClick={() => runAndClose(onCycleLayout)}
      >
        {layout === 'grid' ? <ViewAgenda fontSize="small" /> : <GridView fontSize="small" />}
        <span>{layoutLabel}</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className={`gg-footer-more__item${isChatOpen ? ' is-active' : ''}`}
        onClick={() => runAndClose(onChatToggle)}
      >
        <ChatBubbleOutline fontSize="small" />
        <span>{chatLabel}</span>
      </button>
      {isHost ? (
        <button
          type="button"
          role="menuitem"
          className="gg-footer-more__item"
          onClick={() => runAndClose(onOpenShare)}
        >
          <Share fontSize="small" />
          <span>Share invite link</span>
        </button>
      ) : null}
    </>
  );

  return (
    <footer
      ref={footerRef}
      className={`gg-meeting-footer${compact ? ' gg-meeting-footer--compact' : ''}`}
    >
      <div className="gg-meeting-footer__left">
        <span className="gg-meeting-footer__clock">{formatClock(now)}</span>
        <span className="gg-meeting-footer__sep" aria-hidden>
          |
        </span>
        <span className="gg-meeting-footer__room" title={roomName}>
          {shortRoomCode(roomName)}
        </span>
      </div>

      <div className="gg-meeting-footer__center">
        <button
          type="button"
          className={`gg-ctrl gg-ctrl--round${captionsOn ? ' gg-ctrl--on' : ''}${
            !captionsAvailable && !captionsOn ? ' gg-ctrl--muted' : ''
          }`}
          onClick={onToggleCaptions}
          title={captionsLabel}
          aria-pressed={captionsOn}
          aria-label={captionsLabel}
        >
          <ClosedCaption />
        </button>
        <TrackToggle
          source={Track.Source.Microphone}
          className={`gg-ctrl gg-ctrl--round${!isMicrophoneEnabled ? ' gg-ctrl--off' : ''}`}
          showIcon
          aria-label={micLabel}
          title={micLabel}
        />
        <TrackToggle
          source={Track.Source.Camera}
          className={`gg-ctrl gg-ctrl--round${!isCameraEnabled ? ' gg-ctrl--off' : ''}`}
          showIcon
          aria-label={camLabel}
          title={camLabel}
        />
        <button
          type="button"
          className="gg-ctrl gg-ctrl--round gg-ctrl--end"
          onClick={handleEnd}
          title="Leave meeting"
          aria-label="Leave meeting"
        >
          <CallEnd />
        </button>
        {compact ? (
          <div className="gg-footer-more" ref={moreRef}>
            <button
              type="button"
              className={`gg-ctrl gg-ctrl--round${moreOpen || moreActive ? ' gg-ctrl--on' : ''}`}
              onClick={() => setMoreOpen((open) => !open)}
              title="More actions"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
            >
              <MoreHoriz />
            </button>
            {moreOpen ? (
              <div className="gg-footer-more__menu" role="menu">
                {moreMenuItems}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="gg-meeting-footer__right">
        {compact ? null : secondaryActions}
      </div>
    </footer>
  );
}
